import CustomModal from "../../../common/components/modal/CustomDetailModal";
import { Column, Divider, Padding, Row, SizedBox } from "../../../common/components/CommonBaseComponents";
import { C2Text, C3Text } from "../../../common/components/text/CustomText";
import SecondaryButton from "../../../common/components/button/SecondaryButton";
import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import styled, { createGlobalStyle } from "styled-components";
import QRCodePrintSettingButton from "../components/QRCodePrintSettingButton";
import QRPrintContent from "../components/QRPrintContent";
import { Theme } from "../../../common/Theme";

const PrintOnlyContainer = styled.div`
    display: none;

    @media print {
        display: block;
        width: 100%;
        height: auto;
    }
`;

const PrintItemWrapper = styled.div`
    @media print {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        box-sizing: border-box;

        /* 중요: vh 단위는 인쇄 시 한 페이지의 높이를 기준으로 합니다. */
        height: ${(props) => (100 / props.$printCount).toFixed(2)}vh;

        /* 요소 내부에서 페이지가 잘리지 않도록 설정 */
        page-break-inside: avoid;
        break-inside: avoid;

        /* 설정된 개수 마다 페이지를 넘김 
           단, 마지막 요소일 때는 추가 빈 페이지가 생기지 않도록 제어
        */
        ${({ $printCount, $index, $total }) =>
        ($index + 1) % $printCount === 0 &&
        ($total ? $index + 1 !== $total : true) &&
        `
            page-break-after: always;
            break-after: page;
        `}
    }
`;

const GlobalPrintStyle = createGlobalStyle`
    @media print {
        @page {
            margin: 0;
        }

        /* 1. 기본 앱 영역 및 화면 전용 요소 숨김 */
        #root, 
        nav, 
        aside, 
        header, 
        footer,
        .no-print {
            display: none !important;
        }

        /* 2. 모달 및 조상 요소들의 레이아웃 제약 해제 */
        #modal-root,
        #modal-root > div,
        #modal-root > div > div {
            position: static !important;
            display: block !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
            background: transparent !important;
        }

        /* ModalContainer 내부의 제목(Row)과 구분선(Divider) 숨김 */
        #modal-root > div > div > div:nth-child(1),
        #modal-root > div > div > div:nth-child(2) {
            display: none !important;
        }

        /* ModalContent 및 내부 레이아웃 컴포넌트(Padding, SizedBox) 제약 해제 */
        #modal-root > div > div > div:nth-child(3),
        #modal-root [class*="Padding"],
        #modal-root [class*="SizedBox"] {
            position: static !important;
            display: block !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
        }

        /* 3. 인쇄 영역 최상단 배치 */
        .print-area {
            display: block !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: auto !important;
            visibility: visible !important;
            background: white !important;
        }

        /* 배경색 및 이미지 인쇄 강제 설정 */
        * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }

        /* QR 코드 이미지 가시성 보장 */
        .print-area img {
            display: block !important;
            max-width: 100% !important;
        }
    }
`;

export default function QRCodePrintModal({
    isOpen,
    onClose,
    autoDownload = false,
    devices = [],
    storeId,
    storeName,
}) {
    const qrRefs = useRef([]);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleAllDownload = async () => {
        if (isDownloading) return; // 중복 클릭 방지
        setIsDownloading(true);
        try {
            const zip = new JSZip();
            const imgFolder = zip.folder("qr_codes");

            // 카드를 병렬로 캡처 (순차 대비 속도 개선)
            const blobs = await Promise.all(
                qrRefs.current.filter(Boolean).map(async (element) => {
                    const canvas = await html2canvas(element, {
                        useCORS: true,
                        scale: 2, // 고해상도를 위해 스케일 업
                    });
                    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
                })
            );

            blobs.forEach((blob, i) => {
                if (blob) {
                    const name = devices[i]?.modelNm || devices[i]?.deviceId || `qr_code_${i + 1}`;
                    imgFolder.file(`${name}.png`, blob);
                }
            });

            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, "qr_codes.zip");
        } catch (error) {
            console.error("QR 전체 다운로드 실패:", error);
            alert("QR 코드 다운로드에 실패했습니다. 잠시 후 다시 시도해 주세요.");
        } finally {
            setIsDownloading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    useEffect(() => {
        if (isOpen && autoDownload) {
            handleAllDownload().then(() => {
                onClose();
            });
        }
    }, [isOpen, autoDownload, onClose]);

    const qrCount = devices.length;

    // 프린트 세팅
    const [printCount, setPrintCount] = useState(1);

    return (
        <CustomModal
            title={`LG Laundry Crew ${storeName || ""}`.trim()}
            isOpen={isOpen}
            onClose={onClose}
            maxWidth={"552px"}
            height={"90vh"}
            side={"right"}
            contentPadding={"20px 0"}
        >
            <GlobalPrintStyle />
            <div className="no-print">
                <Row padding={"0 20px 12px 20px"}>
                    <C3Text fontColor={Theme.color.gray500} fontWeight={"400"}>
                        Address
                    </C3Text>
                    <SizedBox width={"8px"} />
                    <C3Text fontWeight={"400"}>123, Lg avenue, New York, NY, 12345</C3Text>
                    <Divider orientation={"vertical"} height={"16px"} marginLeft={"24px"} marginRight={"12px"} />
                    <C3Text fontColor={Theme.color.gray500} fontWeight={"400"}>
                        Phone
                    </C3Text>
                    <SizedBox width={"8px"} />
                    <C3Text fontWeight={"400"}>010-2345-1234</C3Text>
                </Row>
                <Row padding={"0 20px"}>
                    <C3Text fontColor={Theme.color.gray500} fontWeight={"400"}>
                        E-mail
                    </C3Text>
                    <SizedBox width={"8px"} />
                    <C3Text fontWeight={"400"}>mystore@email.com</C3Text>
                </Row>
                <Divider marginTop={"20px"} marginBottom={"20px"} />
            </div>
            <Padding padding={"0 20px"} width={"100%"}>
                <SizedBox
                    width={"100%"}
                    height={"auto"}
                    border={`1px solid ${Theme.color.gray200}`}
                    borderRadius={"16px"}
                    alignItem={"flex-start"}
                    justifyContent={"flex-start"}
                >
                    <div className="no-print" style={{ width: "100%" }}>
                        <Row padding={"11px 20px 0 20px"} width={"100%"} justifyContent={"space-between"}>
                            <C2Text fontWeight={"600"}>Qr Code</C2Text>
                            <Row>
                                <QRCodePrintSettingButton currentValue={printCount} setCurrentValue={setPrintCount} />
                                <SizedBox width={"10px"} />
                                <SecondaryButton
                                    icon={"folder_download"}
                                    text={"All Download"}
                                    onClick={handleAllDownload}
                                    disabled={isDownloading}
                                />
                                <SizedBox width={"10px"} />
                                <SecondaryButton icon={"print"} text={"Print"} onClick={handlePrint} />
                            </Row>
                        </Row>
                        <Divider marginTop={"12px"} marginBottom={"20px"} />
                        <Column alignItem={"center"} width={"100%"} padding={"0 0 20px"} overflowY={"auto"}>
                            {devices.map((device, index) => (
                                <QRPrintContent
                                    key={device.deviceId || index}
                                    ref={(el) => (qrRefs.current[index] = el)}
                                    device={device}
                                    storeId={storeId}
                                    storeName={storeName}
                                />
                            ))}
                        </Column>
                    </div>
                    <PrintOnlyContainer className="print-area">
                        {devices.map((device, index) => (
                            <PrintItemWrapper
                                key={device.deviceId || index}
                                $printCount={printCount}
                                $index={index}
                                $total={qrCount}
                            >
                                <QRPrintContent device={device} storeId={storeId} storeName={storeName} />
                            </PrintItemWrapper>
                        ))}
                    </PrintOnlyContainer>
                </SizedBox>
            </Padding>
        </CustomModal>
    );
}
